import { Star, X } from 'lucide-react';
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './button';
import { Textarea } from './textarea';
import { useCloseTicketWithCsat } from '../../hooks/use-tickets';

interface InlineCsatRatingProps {
  ticketId: string;
}

export function InlineCsatRating({ ticketId }: InlineCsatRatingProps) {
  const [score, setScore] = useState(0);
  const [hoverScore, setHoverScore] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [comment, setComment] = useState('');
  const [popupPosition, setPopupPosition] = useState<{ left: number; top: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const closeWithCsat = useCloseTicketWithCsat();

  const updatePopupPosition = () => {
    if (!triggerRef.current || !popupRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    const width = Math.min(window.innerWidth - gap * 2, popupRef.current.offsetWidth || 288);
    const height = popupRef.current.offsetHeight || 220;
    const left = Math.min(Math.max(triggerRect.left, gap), window.innerWidth - width - gap);
    const belowTop = triggerRect.bottom + gap;
    const top = belowTop + height <= window.innerHeight - gap
      ? belowTop
      : Math.max(gap, triggerRect.top - height - gap);

    setPopupPosition({ left, top, width });
  };

  // Close popup on outside click
  useEffect(() => {
    if (!showPopup) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popupRef.current &&
        triggerRef.current &&
        !popupRef.current.contains(target) &&
        !triggerRef.current.contains(target)
      ) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPopup]);

  useLayoutEffect(() => {
    if (!showPopup) {
      setPopupPosition(null);
      return;
    }
    updatePopupPosition();
  }, [showPopup, score, comment]);

  useEffect(() => {
    if (!showPopup) return;
    const reposition = () => updatePopupPosition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [showPopup]);

  const handleStarClick = (s: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setScore(s);
    setShowPopup(true);
  };

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (score < 1) return;
    closeWithCsat.mutate(
      { id: ticketId, score, comment: comment || undefined },
      {
        onSuccess: () => { setShowPopup(false); setScore(0); setComment(''); },
        onError: (err) => { alert(err instanceof Error ? err.message : 'Eroare la trimiterea feedback-ului'); },
      },
    );
  };

  const displayScore = hoverScore || score;

  return (
    <div ref={triggerRef} className="relative inline-flex items-center" onClick={(e) => e.stopPropagation()}>
      <div className="flex gap-0.5" onMouseLeave={() => setHoverScore(0)}>
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s}
            type="button"
            onMouseEnter={() => setHoverScore(s)}
            onClick={(e) => handleStarClick(s, e)}
            className="rounded p-0.5 transition-colors hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
          >
            <Star
              size={18}
              className={s <= displayScore ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'}
            />
          </button>
        ))}
      </div>

      {showPopup && typeof document !== 'undefined' && createPortal(
        <div
          ref={popupRef}
          role="dialog"
          aria-label="Formular feedback"
          data-testid="inline-csat-popup"
          className="fixed z-[120] max-h-[calc(100vh-1rem)] overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 shadow-xl dark:border-dark-border dark:bg-dark-card"
          style={{
            left: popupPosition?.left ?? 8,
            top: popupPosition?.top ?? 8,
            width: popupPosition?.width ?? 288,
            visibility: popupPosition ? 'visible' : 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium dark:text-dark-text">Feedback — {score} {score === 1 ? 'stea' : 'stele'}</span>
            <button type="button" onClick={(e) => { e.stopPropagation(); setShowPopup(false); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text">
              <X size={14} />
            </button>
          </div>
          <div className="mb-2 flex gap-0.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                type="button"
                onClick={(e) => { e.stopPropagation(); setScore(s); }}
                className="rounded p-0.5 transition-colors hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
              >
                <Star
                  size={22}
                  className={s <= score ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'}
                />
              </button>
            ))}
          </div>
          <Textarea
            placeholder="Comentariu (opțional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="text-sm"
            rows={2}
          />
          <div className="mt-2 flex justify-end">
            <Button
              size="sm"
              disabled={score < 1 || closeWithCsat.isPending}
              onClick={handleSubmit}
            >
              {closeWithCsat.isPending ? 'Se trimite...' : 'Trimite și închide'}
            </Button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
